import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase, safeChannel } from "./supabase";

// One-shot helper: given a list of recipe_slugs, returns a Map<slug,
// publicImageUrl> sourced from the most recent photo for each slug
// across BOTH recipe_photos (manual header uploads — preferred)
// and cook_log_photos (auto from finished cooks — fallback).
// Both tables live in family RLS scope.
async function fetchLatestCookPhotos(slugs) {
  if (!slugs.length) return new Map();

  // Recipe photos first — these are the curated header uploads
  // an adult put on a recipe page. Highest priority for kid tiles.
  const recipeQ = supabase
    .from("recipe_photos")
    .select("storage_path, created_at, recipe_slug, is_primary")
    .in("recipe_slug", slugs)
    .order("is_primary", { ascending: false })
    .order("created_at", { ascending: false })
    .limit(200);

  // Cook log photos as a fallback — most recent cook of each slug.
  const cookQ = supabase
    .from("cook_log_photos")
    .select("storage_path, created_at, cook_logs!inner(recipe_slug)")
    .in("cook_logs.recipe_slug", slugs)
    .order("created_at", { ascending: false })
    .limit(200);

  const [recipeRes, cookRes] = await Promise.all([recipeQ, cookQ]);
  if (recipeRes.error) {
    console.warn("[kid-recipes] recipe_photos:", recipeRes.error.message);
  }
  if (cookRes.error) {
    console.warn("[kid-recipes] cook_log_photos:", cookRes.error.message);
  }

  const bySlug = new Map();
  // Curated header photos win — process first.
  for (const row of (recipeRes.data || [])) {
    if (!row.recipe_slug || bySlug.has(row.recipe_slug)) continue;
    const { data: urlData } = supabase.storage
      .from("meal-photos")
      .getPublicUrl(row.storage_path);
    if (urlData?.publicUrl) bySlug.set(row.recipe_slug, urlData.publicUrl);
  }
  // Cook log photos fill in slugs that don't have a curated header.
  for (const row of (cookRes.data || [])) {
    const slug = row.cook_logs?.recipe_slug;
    if (!slug || bySlug.has(slug)) continue;
    const { data: urlData } = supabase.storage
      .from("meal-photos")
      .getPublicUrl(row.storage_path);
    if (urlData?.publicUrl) bySlug.set(slug, urlData.publicUrl);
  }
  return bySlug;
}

// useKidRecipes — recipes a family adult has marked as "available
// for kids to request". Powers:
//
//   * Adult side: a checklist of recipes the calling adult has
//     marked. mark(slug, title, emoji) / unmark(slug) toggles a
//     row in public.kid_recipes for auth.uid().
//
//   * Kid side: the union of recipes marked by any accepted-family
//     adult of the signed-in kid. Distinct on recipe_slug so two
//     parents marking the same dish doesn't surface twice.
//
// One row per (recipe_slug, marked_by). RLS scopes both directions:
// kids only see family adults' marks; adults only see their own +
// other family adults'.
//
// No long cache. Adults rarely mark/unmark and the realtime
// subscription delivers changes within a second; we don't keep a
// stale list around past the session. Boot fetch is tiny (a few
// rows per family).

export function useKidRecipes(userId, { ownOnly = false } = {}) {
  const [rows, setRows] = useState([]);
  const [photos, setPhotos] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const channelRef = useRef(null);
  const photoChannelRef = useRef(null);

  const load = useCallback(async () => {
    if (!userId) {
      setRows([]);
      setPhotos(new Map());
      setLoading(false);
      return;
    }
    setLoading(true);
    let q = supabase
      .from("kid_recipes")
      .select("id, recipe_slug, recipe_title, recipe_emoji, kid_label, kid_kind, marked_by, marked_at")
      .order("marked_at", { ascending: false });
    if (ownOnly) q = q.eq("marked_by", userId);
    const { data, error } = await q;
    if (error) {
      setError(error);
      console.error("[kid-recipes] load:", error);
    }
    const list = data || [];
    setRows(list);
    setLoading(false);

    // Photo lookup is best-effort. Failure leaves emoji fallback
    // in place; no need to gate the loading state on it.
    const uniqueSlugs = Array.from(new Set(list.map((r) => r.recipe_slug)));
    if (uniqueSlugs.length) {
      const map = await fetchLatestCookPhotos(uniqueSlugs);
      setPhotos(map);
    } else {
      setPhotos(new Map());
    }
  }, [userId, ownOnly]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!userId) return;
    const ch = safeChannel(`rt:kid_recipes:${userId}`)
      .on("postgres_changes",
        { event: "*", schema: "public", table: "kid_recipes" },
        () => load())
      .subscribe();
    channelRef.current = ch;

    // Also listen for new photos — recipe header uploads + cook
    // photos both can change what surfaces on a kid meal tile.
    // Re-running load() is cheap (small selects); matches realtime
    // debouncing the supabase client already does.
    const ph = safeChannel(`rt:kid_recipes_photos:${userId}`)
      .on("postgres_changes",
        { event: "INSERT", schema: "public", table: "cook_log_photos" },
        () => load())
      .on("postgres_changes",
        { event: "*", schema: "public", table: "recipe_photos" },
        () => load())
      .subscribe();
    photoChannelRef.current = ph;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      if (photoChannelRef.current) {
        supabase.removeChannel(photoChannelRef.current);
        photoChannelRef.current = null;
      }
    };
  }, [userId, load]);

  // Adult-side helpers.
  const mark = useCallback(async ({ slug, title, emoji, kidLabel, kidKind }) => {
    if (!userId || !slug) return;
    const payload = {
      recipe_slug:  slug,
      recipe_title: title || slug,
      recipe_emoji: emoji || null,
      marked_by:    userId,
    };
    // Only include kid_label/kid_kind if explicitly provided so the
    // upsert doesn't blow away values set in a prior write that this
    // call didn't intend to touch.
    if (typeof kidLabel === "string") payload.kid_label = kidLabel.trim() || null;
    if (typeof kidKind === "string" && ["meal","snack","dessert"].includes(kidKind)) {
      payload.kid_kind = kidKind;
    }
    const { error } = await supabase.from("kid_recipes").upsert(
      payload,
      { onConflict: "recipe_slug,marked_by" },
    );
    if (error) throw error;
  }, [userId]);

  // Inline edit of an already-marked recipe's label/kind. Lighter
  // than mark() because callers don't need to re-pass title/emoji.
  const updateMeta = useCallback(async ({ slug, kidLabel, kidKind }) => {
    if (!userId || !slug) return;
    const patch = {};
    if (typeof kidLabel === "string") patch.kid_label = kidLabel.trim() || null;
    if (typeof kidKind === "string" && ["meal","snack","dessert"].includes(kidKind)) {
      patch.kid_kind = kidKind;
    }
    if (Object.keys(patch).length === 0) return;
    const { error } = await supabase
      .from("kid_recipes")
      .update(patch)
      .eq("marked_by", userId)
      .eq("recipe_slug", slug);
    if (error) throw error;
  }, [userId]);

  const unmark = useCallback(async (slug) => {
    if (!userId || !slug) return;
    const { error } = await supabase
      .from("kid_recipes")
      .delete()
      .eq("marked_by", userId)
      .eq("recipe_slug", slug);
    if (error) throw error;
  }, [userId]);

  // Kid-side helper: dedupe by slug so the meals list shows each
  // dish once even if multiple family adults marked it. Each row
  // also carries imageUrl when the family has a cook_log_photo for
  // that slug (most recent wins). Display label prefers any adult-
  // provided kid_label (kid's nickname for the dish like "Egg
  // Burgers") over the recipe_title.
  const uniqueBySlug = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const r of rows) {
      if (seen.has(r.recipe_slug)) continue;
      seen.add(r.recipe_slug);
      out.push({
        ...r,
        imageUrl: photos.get(r.recipe_slug) || null,
        displayName: (r.kid_label && r.kid_label.trim()) || r.recipe_title,
      });
    }
    return out;
  }, [rows, photos]);

  // Bucket the kid-side unique-by-slug list by kid_kind so the
  // FoodExplorer can render its 3-segment layout. Default 'meal'
  // when a row was marked before migration 0242 (existing data).
  const uniqueByKind = useMemo(() => {
    const meal = [], snack = [], dessert = [];
    for (const r of uniqueBySlug) {
      const k = r.kid_kind || "meal";
      if      (k === "dessert") dessert.push(r);
      else if (k === "snack")   snack.push(r);
      else                       meal.push(r);
    }
    return { meal, snack, dessert };
  }, [uniqueBySlug]);

  // Adult-side rows also get the image annotation so the manager
  // sheet can preview what kids will see.
  const rowsWithPhotos = useMemo(
    () => rows.map((r) => ({ ...r, imageUrl: photos.get(r.recipe_slug) || null })),
    [rows, photos],
  );

  return {
    rows: rowsWithPhotos,
    uniqueBySlug,
    uniqueByKind,
    photos,
    loading,
    error,
    mark,
    unmark,
    updateMeta,
    refresh: load,
  };
}
